import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { searchProducts } from "../../Services/MenuApi";

// Searchable product dropdown for the Mouvements "Produit" filter.
// Unlike ClientSelector, this does NOT preload the full list on mount —
// products are fetched (debounced) from the server as the user types,
// scoped server-side to products that actually have movement history.
const ProductSelector = ({
  organisation,
  selectedProduct,
  onProductSelect,
  extraSearchParams = {},
  showClearOption = true,
  closeOnSelect = true,
  closedLabel,
  searchPlaceholder,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const runSearch = useCallback(
    async (term) => {
      if (!organisation) return;
      setLoading(true);
      try {
        const response = await searchProducts(organisation, {
          search: term,
          limit: 20,
          ...extraSearchParams,
        });
        if (response.success) {
          setResults(response.data || []);
        }
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setLoading(false);
      }
    },
    [organisation],
  );

  // Fetch on open (shows the first 20 movement-linked products), then
  // re-fetch (debounced) whenever the search term changes while open.
  useEffect(() => {
    if (!showDropdown) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(searchTerm);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm, showDropdown, runSearch]);

  // after
  const handleSelect = (product) => {
    onProductSelect(product);
    setSearchTerm("");
    if (closeOnSelect) {
      setShowDropdown(false);
    }
  };

  const handleClear = () => {
    onProductSelect(null);
    setShowDropdown(false);
    setSearchTerm("");
  };

  const label = (p) => p?.titre?.fr || p?.titre || "";

  return (
    <div
      className="product-selector"
      style={{ position: "relative" }}
      ref={dropdownRef}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          padding: "8px 12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          cursor: "pointer",
        }}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <div className="d-flex align-items-center">
          <i className="fa fa-box mr-2" style={{ color: "#6c757d" }}></i>
          {selectedProduct ? (
            <div className="d-flex align-items-center">
              <span>{label(selectedProduct)}</span>
              {selectedProduct.reference && (
                <span className="badge badge-info ml-2">
                  {selectedProduct.reference}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted">
              {closedLabel || "Tous les produits"}
            </span>
          )}
        </div>
        <i
          className={`fa fa-chevron-${showDropdown ? "up" : "down"}`}
          style={{ fontSize: "12px", color: "#6c757d" }}
        ></i>
      </div>

      {showDropdown && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "white",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            marginTop: "4px",
          }}
        >
          <div className="p-2">
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder={
                searchPlaceholder || "Rechercher un produit (nom, référence)..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {showClearOption && (
                <div
                  className="p-2"
                  style={{
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  onClick={handleClear}
                >
                  <em className="text-muted">Tous les produits</em>
                </div>
              )}

              {loading ? (
                <div className="text-center p-2">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : results.length === 0 ? (
                <div className="text-center text-muted p-2">
                  Aucun produit trouvé
                </div>
              ) : (
                results.map((product) => (
                  <div
                    key={product._id}
                    className="product-item p-2"
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor:
                        selectedProduct?._id === product._id
                          ? "#e3f2fd"
                          : "transparent",
                    }}
                    onClick={() => handleSelect(product)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) => {
                      if (selectedProduct?._id !== product._id) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>{label(product)}</div>
                      {product.reference && (
                        <small className="text-muted">
                          {product.reference}
                        </small>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;
